import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, XCircle, RotateCcw, Receipt } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { formatSeat } from "../../components/SeatMap";
import OrderDetailModal from "../../components/OrderDetailModal";

const SECTIONS = [
  { id: "valid", label: "Valid" },
  { id: "checked_in", label: "Checked in" },
  { id: "pending", label: "Pending" },
  { id: "cancelled", label: "Cancelled" },
];

const channelOf = (t) => (t.is_comp ? "Comp" : t.payment_mode === "manual" ? "Box office" : "Online");
const CHANNEL_STYLE = {
  "Online": "bg-[#eefafa] text-[#1fb0b0]",
  "Box office": "bg-amber-100 text-amber-700",
  "Comp": "bg-gray-100 text-gray-500",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

export default function IssuedVouchersTab() {
  const [tickets, setTickets] = useState(null);
  const [section, setSection] = useState(() => localStorage.getItem("gp_tickets_section") || "valid");
  const [boxOfficeOnly, setBoxOfficeOnly] = useState(() => localStorage.getItem("gp_tickets_box") === "1");
  const [eventFilter, setEventFilter] = useState(() => localStorage.getItem("gp_tickets_event") || "all");
  const [q, setQ] = useState(() => localStorage.getItem("gp_tickets_q") || "");
  const [orderModal, setOrderModal] = useState(null);

  const load = () => api.get("/business/vouchers").then(({ data }) => setTickets(data.vouchers));
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem("gp_tickets_section", section); }, [section]);
  useEffect(() => { localStorage.setItem("gp_tickets_box", boxOfficeOnly ? "1" : "0"); }, [boxOfficeOnly]);
  useEffect(() => { localStorage.setItem("gp_tickets_event", eventFilter); }, [eventFilter]);
  useEffect(() => { localStorage.setItem("gp_tickets_q", q); }, [q]);
  const [cancelling, setCancelling] = useState("");

  const cancelTicket = async (t) => {
    const isBox = t.payment_mode === "manual";
    const verb = isBox ? "Void" : "Cancel";
    if (!window.confirm(`${verb} ${t.attendee_name || t.code}'s ticket? This frees the seat${isBox ? "" : " and refunds the buyer if they paid by card"}.`)) return;
    setCancelling(t.id);
    try {
      const { data } = await api.post(`/business/tickets/${t.code}/cancel`);
      toast({ title: isBox ? "Sale voided" : "Ticket cancelled", description: data.refunded ? "Buyer refunded via Stripe and seat released." : "Seat released." + (data.refund_reason && data.refund_reason !== "not a Stripe payment" ? ` Refund note: ${data.refund_reason}` : "") });
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not cancel", variant: "destructive" });
    } finally { setCancelling(""); }
  };

  const reinstateTicket = async (t) => {
    if (!window.confirm(`Reinstate ${t.attendee_name || t.code}'s ticket? This makes it valid again and re-takes a seat.${t.refunded ? " Note: this ticket was refunded — you'll need to collect payment again yourself." : ""}`)) return;
    setCancelling(t.id);
    try {
      const { data } = await api.post(`/business/tickets/${t.code}/reinstate`);
      toast({ title: "Ticket reinstated", description: data.was_refunded ? "Ticket is valid again. It was previously refunded — collect payment separately." : "Ticket is valid again and the seat has been re-taken." });
      load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not reinstate", variant: "destructive" });
    } finally { setCancelling(""); }
  };

  if (!tickets) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const cat = (t) => t.status || "valid";
  const counts = SECTIONS.reduce((a, s) => ({ ...a, [s.id]: tickets.filter((t) => cat(t) === s.id).length }), {});
  let rows = tickets.filter((t) => cat(t) === section);
  if (boxOfficeOnly) rows = rows.filter((t) => t.payment_mode === "manual" && !t.is_comp);
  const eventOpts = Object.values(tickets.reduce((a, t) => {
    if (t.product_id && !a[t.product_id]) a[t.product_id] = { id: t.product_id, title: t.product_title, event_date: t.event_date, venue_name: t.venue_name };
    return a;
  }, {})).sort((x, y) => (x.event_date || "").localeCompare(y.event_date || ""));
  if (eventFilter !== "all") rows = rows.filter((t) => t.product_id === eventFilter);
  if (q.trim()) { const s = q.trim().toLowerCase(); rows = rows.filter((t) => [t.attendee_name, t.buyer_name, t.buyer_email, t.code].some((f) => (f || "").toLowerCase().includes(s))); }

  return (
    <div data-testid="tickets-tab">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`ticket-section-${s.id}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${section === s.id ? "bg-[#16243b] text-white" : "bg-white text-[#5a6b80] border border-gray-200 hover:border-[#2ec9c9]"}`}>
            {s.label} <span className="opacity-70">({counts[s.id]})</span>
          </button>
        ))}
        <button onClick={() => setBoxOfficeOnly((v) => !v)} data-testid="box-office-filter"
          className={`ml-auto rounded-full px-4 py-2 text-sm font-medium transition-colors ${boxOfficeOnly ? "bg-amber-500 text-white" : "bg-white text-[#5a6b80] border border-gray-200 hover:border-amber-400"}`}>
          Box office only
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="ticket-event-filter"
          className="rounded-full px-4 py-2 text-sm border border-gray-200 bg-white text-[#16243b] outline-none focus:border-[#2ec9c9] max-w-full">
          <option value="all">All events</option>
          {eventOpts.map((o) => <option key={o.id} value={o.id}>{o.title}{o.event_date ? ` — ${fmtDate(o.event_date)}` : ""}{o.venue_name ? ` · ${o.venue_name}` : ""}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ticket-search" placeholder="Search name, email or code…"
          className="flex-1 min-w-[200px] rounded-full px-4 py-2 text-sm border border-gray-200 outline-none focus:border-[#2ec9c9]" />
        <span className="text-xs text-[#8a97a8]">{rows.length} shown</span>
      </div>
      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Code", "Event", "When", "Where", "Tier", "Attendee", "Buyer / order", "Channel", "Price", "Checked in", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((t) => {
              const ch = channelOf(t);
              return (
              <tr key={t.id} className="border-t border-gray-50" data-testid={`ticket-row-${t.id}`}>
                <td className="px-5 py-3 font-mono text-[#16243b]">{t.code}</td>
                <td className="px-5 py-3 text-[#5a6b80]">{t.product_title}</td>
                <td className="px-5 py-3 text-[#8a97a8] whitespace-nowrap">{t.event_date ? fmtDate(t.event_date) : "—"}</td>
                <td className="px-5 py-3 text-[#8a97a8]">{t.venue_name || "—"}</td>
                <td className="px-5 py-3 text-[#5a6b80]">{t.tier_name}{t.seat ? ` · ${formatSeat(t.seat)}` : ""}</td>
                <td className="px-5 py-3 text-[#5a6b80]">{t.attendee_name || "—"}</td>
                <td className="px-5 py-3">
                  {t.order_id ? (
                    <button onClick={() => setOrderModal(t.order_id)} data-testid={`ticket-order-link-${t.id}`}
                      className="text-left group">
                      <span className="block text-[#16243b] font-medium group-hover:text-[#1fb0b0] flex items-center gap-1"><Receipt size={12} className="text-[#2ec9c9]" /> {t.buyer_name || "View order"}</span>
                      <span className="block text-xs text-[#8a97a8]">{t.buyer_email || t.order_id.slice(0, 8)}</span>
                    </button>
                  ) : <span className="text-[#8a97a8]">—</span>}
                </td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${CHANNEL_STYLE[ch]}`}>{ch}{ch === "Box office" && t.pay_method ? ` · ${t.pay_method}` : ""}</span></td>
                <td className="px-5 py-3 text-[#16243b]">£{(t.amount_paid ?? t.original_amount ?? 0).toFixed(2)}{t.refunded ? <span className="ml-1 text-xs text-[#8a97a8]">(refunded)</span> : null}</td>
                <td className="px-5 py-3 text-[#8a97a8]">{t.checked_in_at ? fmtDate(t.checked_in_at) : "—"}</td>
                <td className="px-5 py-3 text-right">
                  {t.status !== "cancelled" ? (
                    <button onClick={() => cancelTicket(t)} disabled={cancelling === t.id} data-testid={`cancel-ticket-${t.id}`}
                      className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs font-medium disabled:opacity-50">
                      {cancelling === t.id ? <Loader2 className="animate-spin" size={13} /> : <XCircle size={14} />} {t.payment_mode === "manual" ? "Void" : "Cancel"}
                    </button>
                  ) : (
                    <button onClick={() => reinstateTicket(t)} disabled={cancelling === t.id} data-testid={`reinstate-ticket-${t.id}`}
                      className="text-[#2ec9c9] hover:text-[#1fb0b0] inline-flex items-center gap-1 text-xs font-medium disabled:opacity-50">
                      {cancelling === t.id ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={14} />} Reinstate
                    </button>
                  )}
                </td>
              </tr>
            );})}
            {rows.length === 0 && <tr><td colSpan={11} className="px-5 py-10 text-center text-[#8a97a8]">No {boxOfficeOnly ? "box-office " : ""}{SECTIONS.find((s) => s.id === section).label.toLowerCase()} tickets.</td></tr>}
          </tbody>
        </table>
      </div>
      {orderModal && <OrderDetailModal orderId={orderModal} manageable onChanged={load} onClose={() => setOrderModal(null)} />}
    </div>
  );
}
